import classNames from "classnames";
import PropTypes from "prop-types";
import {useCallback, useEffect, useLayoutEffect, useRef, useState} from "react";

const useIsomorphicLayoutEffect = typeof window === "undefined" ? useEffect : useLayoutEffect;

/**
 * Keeps a modal shell stable while its internal step changes. The wrapper animates to the measured content height,
 * while a new transitionKey receives a short fade and vertical movement. Nested async content is tracked through
 * ResizeObserver, so loading states can grow into their completed form without snapping the modal.
 */
export default function ModalContentTransition({
    children,
    transitionKey,
    className,
    contentClassName,
    duration,
    offset
}) {
    const frameRef = useRef(null);
    const contentRef = useRef(null);
    const previousKeyRef = useRef(transitionKey);
    const measuredKeyRef = useRef(transitionKey);
    const contentAnimationFrameRef = useRef(null);
    const heightAnimationFrameRef = useRef(null);
    const pendingHeightRef = useRef(null);
    const heightChangeStagedRef = useRef(false);
    const [height, setHeight] = useState(null);
    const [contentVisible, setContentVisible] = useState(true);
    const [reduceMotion, setReduceMotion] = useState(false);

    const measureContent = useCallback((stageChange = false) => {
        const content = contentRef.current;
        const renderedHeight = content?.getBoundingClientRect?.().height ?? 0;
        const nextHeight = Math.max(renderedHeight, content?.scrollHeight ?? 0);
        if (!Number.isFinite(nextHeight) || nextHeight <= 0) return;

        if (heightChangeStagedRef.current) {
            pendingHeightRef.current = nextHeight;
            return;
        }

        if (stageChange && !reduceMotion && typeof window !== "undefined") {
            pendingHeightRef.current = nextHeight;
            heightChangeStagedRef.current = true;
            if (heightAnimationFrameRef.current != null) {
                window.cancelAnimationFrame(heightAnimationFrameRef.current);
            }
            heightAnimationFrameRef.current = window.requestAnimationFrame(() => {
                heightChangeStagedRef.current = false;
                const pendingHeight = pendingHeightRef.current;
                pendingHeightRef.current = null;
                setHeight(current => current === pendingHeight ? current : pendingHeight);
            });
            return;
        }

        setHeight(current => current === nextHeight ? current : nextHeight);
    }, [reduceMotion]);

    useEffect(() => {
        if (typeof window === "undefined" || !window.matchMedia) return undefined;
        const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
        const updatePreference = () => setReduceMotion(mediaQuery.matches);
        updatePreference();
        mediaQuery.addEventListener?.("change", updatePreference);
        return () => mediaQuery.removeEventListener?.("change", updatePreference);
    }, []);

    useIsomorphicLayoutEffect(() => {
        const keyChanged = measuredKeyRef.current !== transitionKey;
        measuredKeyRef.current = transitionKey;
        measureContent(keyChanged);
    }, [children, transitionKey, measureContent]);

    useEffect(() => {
        if (!contentRef.current || typeof ResizeObserver === "undefined") return undefined;
        const observer = new ResizeObserver(() => measureContent());
        observer.observe(contentRef.current);
        return () => observer.disconnect();
    }, [measureContent]);

    useIsomorphicLayoutEffect(() => {
        if (previousKeyRef.current === transitionKey) return undefined;
        previousKeyRef.current = transitionKey;

        if (reduceMotion || typeof window === "undefined") {
            setContentVisible(true);
            return undefined;
        }

        setContentVisible(false);
        contentAnimationFrameRef.current = window.requestAnimationFrame(() => {
            contentAnimationFrameRef.current = window.requestAnimationFrame(() => setContentVisible(true));
        });

        return () => {
            if (contentAnimationFrameRef.current != null) {
                window.cancelAnimationFrame(contentAnimationFrameRef.current);
            }
        };
    }, [transitionKey, reduceMotion]);

    useEffect(() => () => {
        if (heightAnimationFrameRef.current != null && typeof window !== "undefined") {
            window.cancelAnimationFrame(heightAnimationFrameRef.current);
        }
    }, []);

    const motionDuration = reduceMotion ? 0 : duration;

    return (
        <div
            ref={frameRef}
            className={classNames("w-full overflow-hidden", className)}
            data-modal-content-transition=""
            style={{
                height: height == null ? "auto" : `${height}px`,
                transition: motionDuration ? `height ${motionDuration}ms ease-out` : "none"
            }}
        >
            <div
                ref={contentRef}
                className={classNames("flow-root w-full", contentClassName)}
                style={{
                    opacity: contentVisible ? 1 : 0,
                    transform: contentVisible ? "translateY(0)" : `translateY(${offset}px)`,
                    transition: motionDuration
                        ? `opacity ${Math.min(motionDuration, 160)}ms ease-out, transform ${Math.min(motionDuration, 160)}ms ease-out`
                        : "none"
                }}
            >
                {children}
            </div>
        </div>
    );
}

ModalContentTransition.propTypes = {
    children: PropTypes.node,
    transitionKey: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    className: PropTypes.string,
    contentClassName: PropTypes.string,
    duration: PropTypes.number,
    offset: PropTypes.number
};

ModalContentTransition.defaultProps = {
    className: "",
    contentClassName: "",
    duration: 200,
    offset: 4
};
